import { SyntheticEvent, useState } from 'react';

import { useFormContext } from 'react-hook-form';

import { TextField, InputAdornment } from '@mui/material';

import NumberFormat from 'react-number-format';

import ErrorIcon from '@images/svg-icons/errorIcon.svg';
import SuccessIcon from '@images/svg-icons/successIcon.svg';
import CloseIcon from '@images/svg-icons/closeIcon.svg';

import { PersonalDetailsFormStatus, SecondaryFormFields } from '@interfaces/myProfileInterfaces';

import { replaceLabelPlaceholder } from '@utils/pages-utils/EyeExamsUtils';
import { capitalizeFirstLetter, getLabelFallback } from '@utils/utils';

const PersonalDetailsSecondaryForm = (): JSX.Element => {
    const {
        register,
        watch,
        formState: { errors, isSubmitting },
        setValue,
        setError,
        clearErrors,
    } = useFormContext();

    const watchEmail = watch('email');
    const watchFirstPhoneNumber = watch('phoneNumber1');
    const watchSecondPhoneNumber = watch('phoneNumber2');

    const initialFormStatus: PersonalDetailsFormStatus<SecondaryFormFields> = {
        email: {
            focus: false,
            success: watchEmail ? true : false,
        },
        phoneNumber1: {
            focus: false,
            success: watchFirstPhoneNumber ? true : false,
        },
        phoneNumber2: {
            focus: false,
            success: watchSecondPhoneNumber ? true : false,
        },
    };

    const [showFormField, setShowFormField] = useState<boolean>(
        watchSecondPhoneNumber ? true : false
    );
    const [formFieldStatus, setFormFieldStatus] =
        useState<PersonalDetailsFormStatus<SecondaryFormFields>>(initialFormStatus);

    /** The belows function checks if the phone number has fourteen digits */

    const validatePhoneNumber = (eventName: string, phoneNumber: string) => {
        const onlyNumbers = phoneNumber.replace(/\D/g, '');
        if (eventName === 'phoneNumber1' && !phoneNumber.length) {
            return (
                setError('phoneNumber1', {
                    type: 'required',
                    message: replaceLabelPlaceholder(
                        'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                        '{FORM_FIELD}',
                        getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_PHONENUMBER1_LABEL')
                    ),
                }),
                setFormFieldStatus({
                    ...formFieldStatus,
                    phoneNumber1: { success: false, focus: false },
                })
            );
        }
        if (onlyNumbers.length) {
            return onlyNumbers.length < 10
                ? (setError(eventName, {
                      type: 'minLength',
                      message: getLabelFallback(
                          `MYPROFILE_PERSONAL_DETAILS_FORM_INVALID_${eventName.toLocaleUpperCase()}`
                      ),
                  }),
                  setValue(eventName, phoneNumber),
                  setFormFieldStatus({
                      ...formFieldStatus,
                      [eventName]: { success: false, focus: false },
                  }))
                : (setFormFieldStatus({
                      ...formFieldStatus,
                      [eventName]: { success: true, focus: false },
                  }),
                  setValue(eventName, phoneNumber));
        }
    };

    /** The function below validates user email */

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        emailRegex.test(email)
            ? (clearErrors('email'),
              setFormFieldStatus({
                  ...formFieldStatus,
                  email: {
                      success: true,
                      focus: false,
                  },
              }))
            : (setError('email', {
                  type: 'pattern',
                  message: getLabelFallback('MYPROFILE_PERSONAL_DETAILS_INVALID_EMAIL_MESSAGE'),
              }),
              setFormFieldStatus({
                  ...formFieldStatus,
                  email: {
                      success: false,
                      focus: false,
                  },
              }));
        setValue('email', email);
    };

    /** The function below handles the onBlur, onChange e onFocus events for the form validation */

    const handleInputEvent = (e: SyntheticEvent) => {
        e.persist();
        const event = e.target as HTMLInputElement;
        !isSubmitting && clearErrors(event.name);
        if (event.name === 'email') {
            if (e.type === 'blur') {
                if (!event.value) {
                    return (
                        setError(event.name, {
                            type: 'required',
                            message: replaceLabelPlaceholder(
                                'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                                '{FORM_FIELD}',
                                getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_EMAIL_LABEL')
                            ),
                        }),
                        setFormFieldStatus({
                            ...formFieldStatus,
                            email: {
                                success: false,
                                focus: false,
                            },
                        })
                    );
                }

                return validateEmail(event.value);
            }
            event.value
                ? (setFormFieldStatus({
                      ...formFieldStatus,
                      email: {
                          success: false,
                          focus:
                              ['change', 'focus'].includes(e.type) && !errors[event.name]
                                  ? true
                                  : false,
                      },
                  }),
                  setValue(event.name, event.value))
                : (setFormFieldStatus({
                      ...formFieldStatus,
                      email: { success: false, focus: false },
                  }),
                  e.type === 'blur' &&
                      setError(event.name, {
                          type: 'required',
                          message: replaceLabelPlaceholder(
                              'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                              '{FORM_FIELD}',
                              getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_EMAIL_LABEL')
                          ),
                      }),
                  setValue(event.name, ''));
        }
        if (['phoneNumber1', 'phoneNumber2'].includes(event.name)) {
            if (e.type === 'blur') {
                return validatePhoneNumber(event.name, event.value);
            }

            event.value
                ? (setFormFieldStatus({
                      ...formFieldStatus,
                      [event.name]: {
                          focus: ['focus', 'change'].includes(e.type) ? true : false,
                          success: false,
                      },
                  }),
                  setValue(event.name, event.value))
                : (setFormFieldStatus({
                      ...formFieldStatus,
                      [event.name]: {
                          focus: false,
                          success: false,
                      },
                  }),
                  setValue(event.name, ''));
        }
    };

    return (
        <div className="secondary-form">
            <div className="email">
                <span className="secondary-message large bold align-left">
                    {getLabelFallback('MYPROFILE_PERSONAL_DETAILS_SECONDARY_FORM_EMAIL')}
                </span>
                <div className="form-group">
                    <TextField
                        {...register('email')}
                        className={`medium-input-field ${
                            errors?.email?.message
                                ? 'form-field-error'
                                : formFieldStatus.email.success
                                ? 'form-field-success'
                                : ''
                        }`}
                        name="email"
                        variant="outlined"
                        label={capitalizeFirstLetter(
                            getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_EMAIL_LABEL')
                        )}
                        onChange={handleInputEvent}
                        onFocus={handleInputEvent}
                        onBlur={handleInputEvent}
                        helperText={errors?.email?.message}
                        InputProps={{
                            endAdornment: formFieldStatus.email.focus ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <CloseIcon
                                        className="cursor-pointer"
                                        onMouseDown={() => setValue('email', '')}
                                    />
                                </InputAdornment>
                            ) : errors?.email?.message ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <ErrorIcon />
                                </InputAdornment>
                            ) : formFieldStatus.email.success ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <SuccessIcon />
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                </div>
            </div>
            <div className="phone-number">
                <span className="secondary-message large bold align-left">
                    {getLabelFallback('MYPROFILE_PERSONAL_DETAILS_SECONDARY_FORM_PHONE_NUMBER')}
                </span>

                <div className="fields">
                    <div className="form-group">
                        <NumberFormat
                            {...register('phoneNumber1')}
                            format={'(###) ###-####'}
                            customInput={TextField}
                            className={`${
                                errors?.phoneNumber1?.message
                                    ? 'form-field-error'
                                    : formFieldStatus.phoneNumber1.success
                                    ? 'form-field-success'
                                    : ''
                            }`}
                            name="phoneNumber1"
                            variant="outlined"
                            value={watchFirstPhoneNumber}
                            onChange={handleInputEvent}
                            onFocus={handleInputEvent}
                            onBlur={handleInputEvent}
                            label={capitalizeFirstLetter(
                                getLabelFallback(
                                    'MYPROFILE_PERSONAL_DETAILS_FORM_PHONENUMBER1_LABEL'
                                )
                            )}
                            helperText={errors?.phoneNumber1?.message}
                            InputProps={{
                                endAdornment: formFieldStatus.phoneNumber1.focus ? (
                                    <InputAdornment position="end" sx={{ width: '25px' }}>
                                        <CloseIcon
                                            className="cursor-pointer"
                                            onMouseDown={() => setValue('phoneNumber1', '')}
                                        />
                                    </InputAdornment>
                                ) : errors?.phoneNumber1?.message ? (
                                    <InputAdornment position="end" sx={{ width: '25px' }}>
                                        <ErrorIcon />
                                    </InputAdornment>
                                ) : formFieldStatus.phoneNumber1.success ? (
                                    <InputAdornment position="end" sx={{ width: '25px' }}>
                                        <SuccessIcon />
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                    </div>
                    {!showFormField && (
                        <a onClick={() => setShowFormField(true)}>
                            {getLabelFallback(
                                'MYPROFILE_PERSONAL_DETAILS_SECONDARY_FORM_ADD_SECONDARY_LINK'
                            )}
                        </a>
                    )}
                    {showFormField && (
                        <div className="form-group">
                            <NumberFormat
                                {...register('phoneNumber2')}
                                format={'(###) ###-####'}
                                customInput={TextField}
                                className={`${
                                    errors?.phoneNumber2?.message
                                        ? 'form-field-error'
                                        : formFieldStatus.phoneNumber2.success
                                        ? 'form-field-success'
                                        : ''
                                }`}
                                name="phoneNumber2"
                                variant="outlined"
                                value={watchSecondPhoneNumber}
                                onChange={handleInputEvent}
                                onFocus={handleInputEvent}
                                onBlur={handleInputEvent}
                                label={getLabelFallback(
                                    'MYPROFILE_PERSONAL_DETAILS_SECONDARY_FORM_SECOND_PHONE_NUMBER_LABEL'
                                )}
                                helperText={errors?.phoneNumber2?.message}
                                InputProps={{
                                    endAdornment:
                                        formFieldStatus.phoneNumber2.focus &&
                                        watchSecondPhoneNumber ? (
                                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                                <CloseIcon
                                                    className="cursor-pointer"
                                                    onMouseDown={() => setValue('phoneNumber2', '')}
                                                />
                                            </InputAdornment>
                                        ) : errors?.phoneNumber2?.message ? (
                                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                                <ErrorIcon />
                                            </InputAdornment>
                                        ) : formFieldStatus.phoneNumber2.success ? (
                                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                                <SuccessIcon />
                                            </InputAdornment>
                                        ) : null,
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PersonalDetailsSecondaryForm;
